import {OrganisationalSummary, OrganisationalSummaryCategory, PaymentCurrencyType} from "@irati/types"
import {formatCurrency} from "@irati/utils/currency/formatCurrency"

const buildValue = (value: number, currencyCode: PaymentCurrencyType) => {
  return value ? formatCurrency({value, currencyCode, isCents: false}) : "-"
}

const buildCurrency = (
  savingsGBP = 0,
  savingsAUD = 0,
  title: string,
  description: string,
  currency: PaymentCurrencyType,
  linksToFAQ?: boolean
) => {
  const unformattedValue = currency === PaymentCurrencyType.Aud ? savingsAUD : savingsGBP
  const value = buildValue(unformattedValue, currency)

  return {title, description, value, isTrendRate: true, linksToFAQ, currency, unformattedValue}
}

export default (res: OrganisationalSummary, category: OrganisationalSummaryCategory, currency: PaymentCurrencyType) => {
  if (category === OrganisationalSummaryCategory.Perks) {
    const data = res?.perks
    if (!data) {
      return []
    }
    return [
      {
        title: "admin.reporting.orgSummary.perks.engagementRate.title",
        description: "admin.reporting.orgSummary.perks.engagementRate.desc",
        value: data.engagementRate?.value.toFixed(0) || "-",
        isTrendRate: true
      },
      {
        title: "admin.reporting.orgSummary.perks.numRedemptions.title",
        description: "admin.reporting.orgSummary.perks.numRedemptions.desc",
        value: data.redemptions?.value.toFixed(0) || "-"
      },
      {
        title: "admin.reporting.orgSummary.perks.numUsersRedeemed.title",
        description: "admin.reporting.orgSummary.perks.numUsersRedeemed.desc",
        value: data.usersRedeemed?.value.toFixed(0) || "-"
      },
      {
        ...buildCurrency(
          data.savingsGBP?.value || 0,
          data.savingsAUD?.value || 0,
          "admin.reporting.orgSummary.main.totalSavings.title",
          "admin.reporting.orgSummary.perks.totalSavings.desc",
          currency
        ),
        tooltip: "admin.reporting.orgSummary.main.totalSavings.tooltip"
      },
      {
        ...buildCurrency(
          data.digitalVoucherSavingsGbp?.value,
          data.digitalVoucherSavingsAud?.value,
          "admin.reporting.orgSummary.main.fixedSavings.title",
          "admin.reporting.orgSummary.perks.fixedSavings.desc",
          currency
        ),
        tooltip: "admin.reporting.orgSummary.perks.fixedSavings.tooltip"
      },
      {
        ...buildCurrency(
          data.discountVoucherEstimatedSavingsGbp?.value,
          data.discountVoucherEstimatedSavingsAud?.value,
          "admin.reporting.orgSummary.main.estimatedSavings.title",
          "admin.reporting.orgSummary.main.estimatedSavings.desc",
          currency
        ),
        tooltip: "admin.reporting.orgSummary.main.estimatedSavings.tooltip"
      }
    ]
  }
  if (category === OrganisationalSummaryCategory.FlexiPerks) {
    const data = res?.flexi
    if (!data) {
      return []
    }
    return [
      {
        title: "admin.reporting.orgSummary.flexi.engagementRate.title",
        description: "admin.reporting.orgSummary.flexi.engagementRate.desc",
        value: data.engagementRate?.value.toFixed(0) || "-",
        isTrendRate: true
      },
      {
        title: "admin.reporting.orgSummary.flexi.numRedemptions.title",
        description: "admin.reporting.orgSummary.flexi.numRedemptions.desc",
        value: data.redemptions?.value.toFixed(0) || "-"
      },
      {
        title: "admin.reporting.orgSummary.flexi.numUsersRedeemed.title",
        description: "admin.reporting.orgSummary.flexi.numUsersRedeemed.desc",
        value: data.usersRedeemed?.value.toFixed(0) || "-"
      },
      {
        title: "admin.reporting.orgSummary.flexi.earned.title2",
        description: "admin.reporting.orgSummary.flexi.earned.desc",
        value: data.earned?.value.toFixed(0) || "-"
      },
      {
        title: "admin.reporting.orgSummary.flexi.burned.title",
        description: "admin.reporting.orgSummary.flexi.burned.desc",
        value: data.burned?.value.toFixed(0) || "-",
        isTrendRate: true
      },
      buildCurrency(
        data.savingsGBP?.value,
        data.savingsAUD?.value,
        "admin.reporting.orgSummary.flexi.savings.title",
        "admin.reporting.orgSummary.flexi.savings.desc",
        currency
      )
    ]
  }
  if (category === OrganisationalSummaryCategory.Celebrations) {
    const data = res?.celebrations
    if (!data) {
      return []
    }
    return [
      {
        title: "admin.reporting.orgSummary.celebrations.engagementRate.title",
        description: "admin.reporting.orgSummary.celebrations.engagementRate.desc",
        value: data.engagementRate?.value.toFixed(0) || "-",
        isTrendRate: true
      },
      {
        title: "admin.reporting.orgSummary.celebrations.numUsersEngaged.title",
        description: "admin.reporting.orgSummary.celebrations.numUsersEngaged.desc",
        value: data.usersEngaged?.value.toFixed(0) || "-"
      },
      {
        title: "admin.reporting.orgSummary.celebrations.numRecognitions.title",
        description: "admin.reporting.orgSummary.celebrations.numRecognitions.desc",
        value: data.recognitions?.value.toFixed(0) || "-"
      },
      {
        title: "admin.reporting.orgSummary.celebrations.numPolls.title",
        description: "admin.reporting.orgSummary.celebrations.numPolls.desc",
        value: data.polls?.value.toFixed(0) || "-"
      },
      {
        title: "admin.reporting.orgSummary.celebrations.rewardPointsPurchased.title",
        description: "admin.reporting.orgSummary.celebrations.rewardPointsPurchased.desc",
        value: data.rewardPointsPurchased?.value.toFixed(0) || "-",
        isTrendRate: true
      },
      {
        title: "admin.reporting.orgSummary.celebrations.rewardPointsGiven.title",
        description: "admin.reporting.orgSummary.celebrations.rewardPointsGiven.desc",
        value: data.rewardPointsGiven?.value.toFixed(0) || "-",
        isTrendRate: true
      }
    ]
  }
  if (category === OrganisationalSummaryCategory.Wellness) {
    const data = res?.wellness
    if (!data) {
      return []
    }
    return [
      {
        title: "admin.reporting.orgSummary.wellness.engagementRate.title",
        description: "admin.reporting.orgSummary.wellness.engagementRate.desc",
        value: data.engagementRate?.value.toFixed(0) || "-",
        isTrendRate: true
      },
      {
        title: "admin.reporting.orgSummary.wellness.numUsersEngaged.title",
        description: "admin.reporting.orgSummary.wellness.numUsersEngaged.desc",
        value: data.usersEngaged?.value.toFixed(0) || "-",
        isTrendRate: true
      },
      {
        title: "admin.reporting.orgSummary.wellness.numViews.title",
        description: "admin.reporting.orgSummary.wellness.numViews.desc",
        value: data.views?.value.toFixed(0) || "-"
      }
    ]
  }
  const data = res?.main
  if (!data) {
    return []
  }

  return [
    {
      title: "admin.reporting.orgSummary.main.engagementRate.title",
      description: "admin.reporting.orgSummary.main.engagementRate.desc",
      value: data.engagementRate?.value.toFixed(0) || "-",
      isTrendRate: true
    },
    {
      title: "admin.reporting.orgSummary.main.numUsersEngaged.title",
      description: "admin.reporting.orgSummary.main.numUsersEngaged.desc",
      value: data.numberOfEngagedUsers?.value.toFixed(0) || "-",
      linksToFAQ: true
    },
    {
      title: "admin.reporting.orgSummary.main.numEngagements.title",
      description: "admin.reporting.orgSummary.main.numEngagements.desc",
      value: data.numberOfEngagements?.value.toFixed(0) || "-",
      linksToFAQ: true
    },
    {
      ...buildCurrency(
        data.savingsGBP?.value || 0,
        data.savingsAUD?.value || 0,
        "admin.reporting.orgSummary.main.totalSavings.title",
        "admin.reporting.orgSummary.main.totalSavings.desc",
        currency
      ),
      tooltip: "admin.reporting.orgSummary.main.totalSavings.tooltip"
    },
    {
      ...buildCurrency(
        data.fixedSavingsGBP?.value,
        data.fixedSavingsAUD?.value,
        "admin.reporting.orgSummary.main.fixedSavings.title",
        "admin.reporting.orgSummary.main.fixedSavings.desc",
        currency
      ),
      tooltip: "admin.reporting.orgSummary.main.fixedSavings.tooltip"
    },
    {
      ...buildCurrency(
        data.estimatedSavingsGBP?.value,
        data.estimatedSavingsAUD?.value,
        "admin.reporting.orgSummary.main.estimatedSavings.title",
        "admin.reporting.orgSummary.main.estimatedSavings.desc",
        currency
      ),
      tooltip: "admin.reporting.orgSummary.main.estimatedSavings.tooltip"
    }
  ]
}
